import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from './SearchableSelect';

const FilmRates = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [filmRates, setFilmRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingRates, setEditingRates] = useState({});

  // Fetch all customers for dropdown
  const fetchCustomers = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        'https://app-pune-ndt.cloud/api/film-rates/customers',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch customers');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    }
  };

  // Fetch film rates for selected customer
  const fetchFilmRates = async (customerId) => {
    setLoading(true);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/film-rates/customer/${customerId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setFilmRates(response.data.data);
        // Initialize editing rates with current rates
        const initialRates = {};
        response.data.data.forEach(rate => {
          initialRates[rate.film_size_id] = rate.current_rate;
        });
        setEditingRates(initialRates);
      }
    } catch (err) {
      setError('Failed to fetch film rates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Handle customer selection
  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    setSelectedCustomer(customerId);
    setError('');
    setSuccess('');

    if (customerId) {
      fetchFilmRates(customerId);
    } else {
      setFilmRates([]);
      setEditingRates({});
    }
  };

  // Handle rate change
  const handleRateChange = (filmSizeId, value) => {
    setEditingRates({
      ...editingRates,
      [filmSizeId]: value
    });
  };

  // Save all rates for selected customer
  const handleSaveRates = async () => {
    if (!selectedCustomer) {
      setError('Please select a customer');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('token');

      // Prepare rates array
      const rates = Object.keys(editingRates).map(filmSizeId => ({
        film_size_id: parseInt(filmSizeId),
        rate_per_film: parseFloat(editingRates[filmSizeId])
      }));

      const response = await axios.post(
        'https://app-pune-ndt.cloud/api/film-rates/bulk',
        {
          customer_id: parseInt(selectedCustomer),
          rates: rates
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess('Film rates updated successfully!');
        fetchFilmRates(selectedCustomer);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update film rates';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Reset rate to default
  const handleResetRate = async (rateId, filmSizeId) => {
    if (!rateId) {
      // No custom rate set, just reset the input to default
      const filmRate = filmRates.find(r => r.film_size_id === filmSizeId);
      if (filmRate) {
        setEditingRates({
          ...editingRates,
          [filmSizeId]: filmRate.default_film_rate
        });
      }
      return;
    }

    if (!window.confirm('Reset this rate to default rate?')) {
      return;
    }

    try {
      const token = Cookies.get('token');
      const response = await axios.delete(
        `https://app-pune-ndt.cloud/api/film-rates/${rateId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess('Rate reset to default!');
        fetchFilmRates(selectedCustomer);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to reset rate';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Film Rates</h1>
          {/* <p className="text-gray-600 mt-1">Set custom film rates for each customer</p> */}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Customer Selection */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <label className="block text-lg font-medium text-gray-700 mb-3">
            Select Customer *
          </label>
          <SearchableSelect
            options={customers}
            value={selectedCustomer}
            onChange={(id) => handleCustomerChange({ target: { value: id } })}
            placeholder="-- Select a customer --"
          />
        </div>

        {/* Film Rates Table */}
        {selectedCustomer && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-indigo-900 to-indigo-900">
              <h2 className="text-2xl font-bold text-white">
                Film Rates for: {customers.find(c => c.id === parseInt(selectedCustomer))?.name}
              </h2>
              <p className="text-white text-sm mt-1">
                Total Film Sizes: {filmRates.length}
              </p>
            </div>

            {loading ? (
              <div className="p-8 text-center text-gray-500">
                Loading film rates...
              </div>
            ) : filmRates.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No film sizes available
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sr. No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Film Size
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Film Brand
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Square Inch
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Default Rate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rate Per Film (₹)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filmRates.map((filmRate, index) => (
                        <tr key={filmRate.film_size_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {filmRate.film_size}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                              {filmRate.film_brand}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {parseFloat(filmRate.square_inch_size).toFixed(2)} sq.in
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="text-gray-600">
                              ₹{parseFloat(filmRate.default_film_rate).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={editingRates[filmRate.film_size_id] || ''}
                              onChange={(e) => handleRateChange(filmRate.film_size_id, e.target.value)}
                              className={`w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${filmRate.rate_per_film ? 'border-gray-300 font-semibold' : 'border-indigo-900'
                                }`}
                              placeholder="Enter rate"
                            />
                            {filmRate.rate_per_film && (
                              <span className="ml-2 text-xs text-indigo-900 font-medium">
                                (Rate already applied)
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleResetRate(filmRate.rate_id, filmRate.film_size_id)}
                              className="text-blue-600 hover:text-blue-900 font-medium"
                              title="Reset to default rate"
                            >
                              🔄 Reset
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Save Button */}
                <div className="p-6 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={handleSaveRates}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-indigo-900 to-indigo-900 text-white px-6 py-4 rounded-lg font-medium text-lg hover:from-blue-700 hover:to-indigo-700 transition duration-300 disabled:opacity-50 shadow-lg"
                  >
                    {loading ? 'Saving...' : '💾 Save All Film Rates'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilmRates;